import React, { useEffect, useState } from "react";

interface LogoRulerProps {
  containerRef: React.RefObject<HTMLDivElement>;
  logoRef: React.RefObject<HTMLImageElement>;
  widthMm: number;
  heightMm: number;
}

export const LogoRuler: React.FC<LogoRulerProps> = ({
  containerRef,
  logoRef,
  widthMm,
  heightMm,
}) => {
  const [logoRect, setLogoRect] = useState<DOMRect | null>(null);
  const [containerRect, setContainerRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    const updateRects = () => {
      if (containerRef.current && logoRef.current) {
        setContainerRect(containerRef.current.getBoundingClientRect());
        setLogoRect(logoRef.current.getBoundingClientRect());
      }
    };

    // Initial update
    updateRects();

    // Observe resizing of both elements
    const resizeObserver = new ResizeObserver(updateRects);
    if (containerRef.current) resizeObserver.observe(containerRef.current);
    if (logoRef.current) resizeObserver.observe(logoRef.current);

    // Also listen to window resize as layout might change
    window.addEventListener("resize", updateRects);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateRects);
    };
  }, [containerRef, logoRef]);

  if (!logoRect || !containerRect) return null;

  // Calculate relative position
  const left = logoRect.left - containerRect.left;
  const top = logoRect.top - containerRect.top;
  const width = logoRect.width;
  const height = logoRect.height;

  // Pixels per mm
  const pxPerMmX = width / widthMm;
  const pxPerMmY = height / heightMm;

  // Ruler height/width
  const RULER_SIZE = 24; // 24px (h-6 / w-6)

  // Tick rendering helper
  const renderTicks = (
    lengthMm: number,
    pxPerMm: number,
    isHorizontal: boolean
  ) => {
    const ticks = [];
    // Only render ticks if we have a valid pxPerMm ratio
    if (!isFinite(pxPerMm) || pxPerMm <= 0) return null;

    // Visibility thresholds to prevent overcrowding
    const showMinor = pxPerMm > 2; // Only show 1mm ticks if > 2px apart
    
    // Adjust loop limit to avoid floating point issues near the end
    const limit = Math.floor(lengthMm);

    for (let i = 0; i <= limit; i++) {
      const isMajor = i % 10 === 0;
      const isMedium = i % 5 === 0;

      // Skip minor ticks if too dense
      if (!isMajor && !isMedium && !showMinor) continue;

      // Determine tick length based on type
      let tickLength = 4; // Minor: 4px
      if (isMajor) tickLength = 14; // Major: 14px
      else if (isMedium) tickLength = 9; // Medium: 9px

      const pos = i * pxPerMm;

      if (isHorizontal) {
        ticks.push(
          <line
            key={i}
            x1={pos}
            y1={RULER_SIZE}
            x2={pos}
            y2={RULER_SIZE - tickLength}
            stroke="#9CA3AF" // gray-400
            strokeWidth={1}
            vectorEffect="non-scaling-stroke" // Keep line width consistent
          />
        );
      } else {
        ticks.push(
          <line
            key={i}
            x1={RULER_SIZE}
            y1={pos}
            x2={RULER_SIZE - tickLength}
            y2={pos}
            stroke="#9CA3AF" // gray-400
            strokeWidth={1}
            vectorEffect="non-scaling-stroke"
          />
        );
      }
    }
    return ticks;
  };

  return (
    <>
      {/* Corner Junction */}
      <div className="absolute top-0 left-0 w-6 h-6 bg-gray-50 border-r border-b border-gray-300 z-20 pointer-events-none" />

      {/* Top Ruler */}
      <div className="absolute top-0 left-0 right-0 h-6 bg-gray-50 border-b border-gray-300 z-10 pointer-events-none overflow-hidden">
        <div
          className="relative w-full h-full"
          style={{ transform: `translateX(${left}px)` }}
        >
          <svg
            width={width}
            height={RULER_SIZE}
            className="overflow-visible block"
          >
            {renderTicks(widthMm, pxPerMmX, true)}
          </svg>

          {/* Start Marker (0mm) */}
          <span
            className="absolute top-0 text-[10px] leading-none text-gray-500 font-medium bg-gray-50/80 px-0.5 rounded"
            style={{ left: 0, transform: "translateX(-50%)" }}
          >
            0mm
          </span>
          {/* End Marker (Width) */}
          <span
            className="absolute top-0 text-[10px] leading-none text-gray-500 font-medium bg-gray-50/80 px-0.5 rounded"
            style={{ left: `${width}px`, transform: "translateX(-50%)" }}
          >
            {widthMm}mm
          </span>
        </div>
      </div>

      {/* Left Ruler */}
      <div className="absolute top-0 left-0 bottom-0 w-6 bg-gray-50 border-r border-gray-300 z-10 pointer-events-none overflow-hidden">
        <div
          className="relative w-full h-full"
          style={{ transform: `translateY(${top}px)` }}
        >
          <svg
            width={RULER_SIZE}
            height={height}
            className="overflow-visible block"
          >
            {renderTicks(heightMm, pxPerMmY, false)}
          </svg>

          {/* Start Marker (0mm) */}
          <span
            className="absolute left-0 text-[10px] leading-none text-gray-500 font-medium bg-gray-50/80 px-0.5 rounded"
            style={{ top: 0, writingMode: "vertical-rl", transform: "translateY(-50%)" }}
          >
            0mm
          </span>

          {/* End Marker (Height) */}
          <span
            className="absolute left-0 text-[10px] leading-none text-gray-500 font-medium bg-gray-50/80 px-0.5 rounded"
            style={{
              top: `${height}px`,
              writingMode: "vertical-rl",
              transform: "translateY(-50%)",
            }}
          >
            {heightMm}mm
          </span>
        </div>
      </div>
    </>
  );
};
